import { compileSpecStream, createMixedStreamParser, type Spec, type SpecStreamLine } from "@json-render/core";

const SPEC_FENCE_PATTERN = /```spec\s*\n([\s\S]*?)```/g;
const LEGACY_M360_FENCE_PATTERN = /```m360\s*\n([\s\S]*?)```/g;
const TOOL_RESULTS_FENCE_PATTERN = /```tool-results\s*\n([\s\S]*?)```/;

export type ParsedAgentMessage = {
  prose: string;
  spec: Spec | null;
  toolResults: string | null;
};

/** Strip server-side tool markdown preserved for card fallback; not shown to staff. */
export function extractToolResults(text: string): { text: string; toolResults: string | null } {
  const match = text.match(TOOL_RESULTS_FENCE_PATTERN);
  if (!match) {
    return { text, toolResults: null };
  }

  return {
    text: text.replace(TOOL_RESULTS_FENCE_PATTERN, "").trim(),
    toolResults: match[1]?.trim() || null
  };
}

function normalizeProse(text: string): string {
  return text.replace(/\n{3,}/g, "\n\n").trim();
}

function compileSpecFromFence(body: string): Spec | null {
  const trimmed = body.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const compiled = compileSpecStream(trimmed) as unknown as Spec;
    if (!compiled?.root && !compiled?.elements) {
      return null;
    }
    return compiled;
  } catch {
    return null;
  }
}

function parseWithMixedStreamParser(text: string): ParsedAgentMessage {
  let prose = "";
  const patchLines: string[] = [];

  const parser = createMixedStreamParser({
    onText: (chunk) => {
      prose += chunk;
    },
    onPatch: (patch: SpecStreamLine) => {
      patchLines.push(JSON.stringify(patch));
    }
  });

  parser.push(text);
  parser.flush();

  const spec = patchLines.length > 0 ? compileSpecFromFence(patchLines.join("\n")) : null;

  return {
    prose: normalizeProse(prose),
    spec,
    toolResults: null
  };
}

/** Split assistant text into markdown prose and a json-render spec (```spec fence or inline stream). */
export function parseAgentMessage(text: string): ParsedAgentMessage {
  const { text: messageBody, toolResults } = extractToolResults(text);
  const trimmed = messageBody.trim();
  if (!trimmed) {
    return { prose: "", spec: null, toolResults };
  }

  if (SPEC_FENCE_PATTERN.test(trimmed)) {
    SPEC_FENCE_PATTERN.lastIndex = 0;
    let spec: Spec | null = null;
    const proseParts: string[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = SPEC_FENCE_PATTERN.exec(trimmed)) !== null) {
      if (match.index > lastIndex) {
        proseParts.push(trimmed.slice(lastIndex, match.index));
      }
      const fenceBody = match[1] ?? "";
      const compiled = compileSpecFromFence(fenceBody);
      if (compiled) {
        spec = compiled;
      } else if (fenceBody.trim()) {
        proseParts.push(`\`\`\`spec\n${fenceBody}\`\`\``);
      }
      lastIndex = match.index + match[0].length;
    }

    const trailing = trimmed.slice(lastIndex);
    if (trailing) {
      proseParts.push(trailing);
    }

    return {
      prose: normalizeProse(proseParts.join("\n\n")),
      spec,
      toolResults
    };
  }

  if (!trimmed.includes("```spec") && /^\s*\{\s*"op"\s*:/m.test(trimmed)) {
    return { ...parseWithMixedStreamParser(trimmed), toolResults };
  }

  return { prose: trimmed, spec: null, toolResults };
}

/** Detect legacy m360 fences so we can still render older assistant replies during rollout. */
export function hasLegacyM360Fences(text: string): boolean {
  LEGACY_M360_FENCE_PATTERN.lastIndex = 0;
  return LEGACY_M360_FENCE_PATTERN.test(text);
}
